import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Fuel, Settings, ShieldCheck, MapPin, Wind, Calendar as CalIcon, CheckCircle2, Plane, Building2, Zap, Shield, Crown, Languages, Star, Clock } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import AgencyLodgingsStrip from "@/components/AgencyLodgingsStrip";

const BADGE_META = {
  airport_delivery: { label: "Livraison aéroport", icon: Plane, color: "bg-sky-100 text-sky-700" },
  hotel_delivery: { label: "Livraison hôtel", icon: Building2, color: "bg-violet-100 text-violet-700" },
  instant_booking: { label: "Réservation instantanée", icon: Zap, color: "bg-amber-100 text-amber-800" },
  insurance_included: { label: "Assurance incluse", icon: Shield, color: "bg-emerald-100 text-emerald-700" },
  premium: { label: "Premium", icon: Crown, color: "bg-[#d4a437]/15 text-[#0a1e3f]" },
};

const RT_LABEL = { day: "Jour", week: "Semaine", month: "Mois" };

const POLICY_LABELS = {
  flexible: "Flexible — Annulation gratuite jusqu'à 24h avant la remise",
  moderate: "Modérée — Annulation gratuite jusqu'à 5 jours avant la remise",
  strict: "Stricte — Aucun remboursement en cas d'annulation",
};

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fmt } = useCurrency();
  const { t } = useI18n();
  const [v, setV] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(0);

  useEffect(() => {
    api.get(`/vehicles/${id}`).then((r) => setV(r.data));
  }, [id]);

  if (!v) return (
    <div className="min-h-screen bg-[#f8f9fa]"><SiteHeader /><div className="p-24 text-center text-gray-500">Chargement…</div></div>
  );

  const photos = v.photos && v.photos.length > 0 ? v.photos : ["https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=1200"];
  const unavailable = (v.unavailable_dates || []).map((d) => new Date(d));

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <SiteHeader />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <Link to="/search" className="text-sm text-gray-500 hover:text-[#0a1e3f]" data-testid="back-to-search">← Retour aux résultats</Link>
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl overflow-hidden mbk-card-shadow border border-gray-100">
              <div className="h-96 bg-gray-100 relative">
                <img src={photos[selectedPhoto]} alt={v.name} className="w-full h-full object-cover" />
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur text-[#0a1e3f] text-xs font-bold px-3 py-1 rounded-full">
                  {selectedPhoto + 1} / {photos.length}
                </div>
              </div>
              {photos.length > 1 && (
                <div className="p-4 flex gap-3 overflow-auto">
                  {photos.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedPhoto(i)}
                      className={`h-20 w-28 rounded-xl overflow-hidden flex-shrink-0 border-2 ${i === selectedPhoto ? "border-[#0a1e3f]" : "border-transparent"}`}
                    >
                      <img src={p} className="w-full h-full object-cover" alt="" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 bg-white rounded-3xl p-6 border border-gray-100 mbk-card-shadow">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h1 className="font-display text-3xl font-black text-[#0a1e3f]">{v.name}</h1>
                  <div className="text-sm text-gray-500 mt-1">
                    Agence : <span className="font-semibold text-[#0a1e3f]">{v.agency?.name}</span>
                    {v.agency?.verified && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[#0e7a3f] text-xs font-bold">
                        <ShieldCheck className="h-3.5 w-3.5" /> Agence vérifiée
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" /> {v.agency?.city}, {v.agency?.country}
                  </div>
                </div>
                <Badge className="bg-[#dcfce7] text-[#0e7a3f] hover:bg-[#dcfce7] rounded-full px-3 py-1">Disponible</Badge>
              </div>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <Feature icon={Users} label={`${v.seats} places`} />
                <Feature icon={Settings} label={v.transmission} />
                <Feature icon={Fuel} label={v.fuel} />
                <Feature icon={Wind} label={v.ac ? "Climatisation" : "Sans clim"} />
                <Feature icon={CalIcon} label={`Année ${v.year}`} />
                <Feature icon={CheckCircle2} label={`${v.category}`} />
              </div>
              {(v.badges?.length > 0 || v.rental_types?.length > 0) && (
                <div className="mt-6 flex flex-wrap gap-2" data-testid="vehicle-badges-detail">
                  {(v.badges || []).map((b) => {
                    const m = BADGE_META[b];
                    if (!m) return null;
                    const Icon = m.icon;
                    return (
                      <span key={b} className={`text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-1.5 ${m.color}`}>
                        <Icon className="h-3.5 w-3.5" /> {m.label}
                      </span>
                    );
                  })}
                  {(v.rental_types || []).map((rt) => (
                    <span key={rt} className="text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-1.5 bg-[#dcfce7] text-[#0e7a3f]">
                      Location au {RT_LABEL[rt] || rt}
                    </span>
                  ))}
                  {v.min_duration_days > 1 && (
                    <span className="text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-1.5 bg-gray-100 text-[#0a1e3f]">
                      Min. {v.min_duration_days} jours
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-3xl p-6 border border-gray-100 mbk-card-shadow">
                <h3 className="font-display text-lg font-bold text-[#0a1e3f]">Description</h3>
                <p className="text-sm text-gray-600 leading-relaxed mt-2">{v.description || "Aucune description fournie."}</p>
              </div>
              <div className="bg-white rounded-3xl p-6 border border-gray-100 mbk-card-shadow">
                <h3 className="font-display text-lg font-bold text-[#0a1e3f]">Caractéristiques</h3>
                <dl className="mt-3 text-sm divide-y divide-gray-100">
                  <Row k="Marque" v={v.brand} />
                  <Row k="Modèle" v={v.model} />
                  <Row k="Année" v={v.year} />
                  <Row k="Couleur" v={v.color || "-"} />
                  <Row k="Carburant" v={v.fuel} />
                  <Row k="Boîte" v={v.transmission} />
                  <Row k="Kilométrage" v={v.mileage ? `${v.mileage} km` : "-"} />
                </dl>
              </div>
            </div>

            <div className="mt-6 bg-white rounded-3xl p-6 border border-gray-100 mbk-card-shadow">
              <h3 className="font-display text-lg font-bold text-[#0a1e3f]">Calendrier de disponibilité</h3>
              <div className="mt-3">
                <Calendar
                  mode="single"
                  disabled={unavailable}
                  className="rounded-xl"
                />
                <div className="flex gap-4 mt-3 text-xs">
                  <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-[#0e7a3f]/20 border border-[#0e7a3f]" />Disponible</span>
                  <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-gray-200" />Indisponible</span>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-white rounded-3xl p-6 border border-gray-100 mbk-card-shadow">
              <h3 className="font-display text-lg font-bold text-[#0a1e3f]">Conditions de location</h3>
              <p className="text-sm text-gray-600 leading-relaxed mt-2">{v.conditions || "Documents requis : pièce d'identité, permis de conduire. Acompte à la réservation, solde à la remise du véhicule."}</p>
              {v.agency?.cancellation_policy && (
                <div className="mt-4 border-t pt-4" data-testid="cancellation-policy">
                  <div className="text-xs uppercase tracking-widest text-[#0e7a3f] font-bold mb-2">Politique d'annulation</div>
                  <p className="text-sm text-gray-700">
                    {v.agency.cancellation_policy === "custom"
                      ? (v.agency.cancellation_custom_text || "Contactez l'agence pour les conditions d'annulation.")
                      : (POLICY_LABELS[v.agency.cancellation_policy] || POLICY_LABELS.moderate)}
                  </p>
                </div>
              )}
            </div>

            {v.with_driver_available && (
              <div className="mt-6 bg-white rounded-3xl p-6 border border-gray-100 mbk-card-shadow" data-testid="driver-info-card">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#0e7a3f]" />
                  <h3 className="font-display text-lg font-bold text-[#0a1e3f]">Location avec chauffeur</h3>
                </div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  {v.driver_experience_years > 0 && (
                    <div className="flex items-start gap-2">
                      <Star className="h-4 w-4 text-[#d4a437] mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-xs text-gray-500">Expérience</div>
                        <div className="font-semibold text-[#0a1e3f]">{v.driver_experience_years} ans</div>
                      </div>
                    </div>
                  )}
                  {v.driver_languages && v.driver_languages.length > 0 && (
                    <div className="flex items-start gap-2">
                      <Languages className="h-4 w-4 text-[#0e7a3f] mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-xs text-gray-500">Langues parlées</div>
                        <div className="font-semibold text-[#0a1e3f]">{v.driver_languages.join(", ")}</div>
                      </div>
                    </div>
                  )}
                  {v.driver_availability && (
                    <div className="flex items-start gap-2">
                      <Clock className="h-4 w-4 text-[#0a1e3f] mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-xs text-gray-500">Disponibilité</div>
                        <div className="font-semibold text-[#0a1e3f]">{v.driver_availability}</div>
                      </div>
                    </div>
                  )}
                  {v.driver_price_per_day > 0 && (
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-[#0e7a3f] mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-xs text-gray-500">Supplément</div>
                        <div className="font-semibold text-[#0a1e3f]">{fmt(v.driver_price_per_day)}/jour</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="bg-white rounded-3xl p-6 border border-gray-100 mbk-card-shadow">
              <div className="text-3xl font-black text-[#0a1e3f]">{fmt(v.price_per_day)}</div>
              <div className="text-xs text-gray-500 -mt-1">par jour</div>
              <div className="mt-4 bg-[#dcfce7] text-[#0e7a3f] rounded-xl p-3 text-xs font-semibold inline-flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <div>Agence vérifiée</div>
                  <div className="text-[#0e7a3f]/80 font-normal">Réservation 100% sécurisée</div>
                </div>
              </div>
              <Button
                data-testid="book-now-btn"
                onClick={() => navigate(`/booking/${v.id}`)}
                className="mbk-btn-green w-full h-12 mt-5 font-semibold text-white"
              >
                Réserver maintenant
              </Button>
              <div className="mt-4 text-xs text-gray-500 text-center">
                {v.deposit_mode === "fixed" && v.deposit_fixed > 0
                  ? <>Acompte fixe {fmt(v.deposit_fixed)} · Solde à la remise du véhicule</>
                  : <>Acompte {v.deposit_percent}% · Solde à la remise du véhicule</>}
              </div>
            </div>

            {v.agency && (
              <div className="mt-4 bg-white rounded-3xl p-6 border border-gray-100 mbk-card-shadow">
                <h4 className="font-display text-base font-bold text-[#0a1e3f]">À propos de l'agence</h4>
                <div className="mt-3 flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-[#0a1e3f] text-[#d4a437] font-black flex items-center justify-center">{v.agency.name?.[0]}</div>
                  <div>
                    <div className="font-semibold text-[#0a1e3f]">{v.agency.name}</div>
                    <div className="text-xs text-[#0e7a3f] font-semibold">Agence vérifiée</div>
                    {v.agency.rating > 0 && (
                      <div className="text-xs text-[#0a1e3f] font-bold flex items-center gap-1 mt-0.5" data-testid="agency-detail-rating">
                        <Star className="h-3 w-3 fill-[#d4a437] text-[#d4a437]" />
                        {Number(v.agency.rating).toFixed(1)}
                        <span className="text-gray-400 font-normal">({v.agency.reviews_count || 0} avis)</span>
                      </div>
                    )}
                  </div>
                </div>
                <p className="mt-3 text-xs text-gray-500">{v.agency.description || "Partenaire officiel"}</p>
                {v.agency.phone && <p className="mt-2 text-xs text-gray-600">📞 {v.agency.phone}</p>}
              </div>
            )}
            {v.agency && (
              <AgencyLodgingsStrip agencyId={v.agency.id} title="Aussi proposé par cette agence" limit={3} compact />
            )}
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

const Feature = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 text-[#0a1e3f]">
    <Icon className="h-4 w-4 text-[#0e7a3f]" />
    <span className="font-medium">{label}</span>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between py-2">
    <span className="text-gray-500">{k}</span>
    <span className="font-semibold text-[#0a1e3f]">{v}</span>
  </div>
);
